/**
 * Pirate Gallery Service
 * Handles all REST API calls for the Pirate Sail Design Competition.
 *
 * Endpoints (WordPress mu-plugin: pirate-gallery.php):
 *   GET    /custom/v1/pirate-flags              — approved entries (public, website)
 *   GET    /custom/v1/pirate-flags/screen     — open API for digital signage (same data + metadata)
 *   GET    /custom/v1/open/pirate-flag-gallery  — alias of /pirate-flags/screen
 *   POST   /custom/v1/pirate-flags              — submit new entry  (public)
 *   GET    /custom/v1/pirate-flags/all          — all entries      (admin)
 *   POST   /custom/v1/pirate-flags/{id}/status  — update status (admin)
 *   DELETE /custom/v1/pirate-flags/{id}         — delete entry     (admin)
 *
 * Live open-API base (production): https://www.belvoircastle.com/wp/wp-json/custom/v1
 */

import config from '../config/environment';

const API_BASE = `${config.apiEndpoint}/custom/v1`;

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

async function handleResponse(response) {
  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Server returned non-JSON response (${response.status}): ${text.slice(0, 200)}`);
  }
  if (!response.ok) {
    const message =
      json?.message || json?.data?.message || `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(message);
  }
  return json;
}

/**
 * Fetch all approved sail design entries for the public gallery.
 * Returns an array of entry objects.
 */
export async function getApprovedFlags() {
  const response = await fetch(`${API_BASE}/pirate-flags`, {
    method: 'GET',
    headers,
  });
  const json = await handleResponse(response);
  return json.data ?? [];
}

/**
 * Open read-only feed for digital screens / external apps (approved entries only).
 * Response shape: { ok, api, version, generatedAt, count, items[] }.
 * Each item: { id, imageUrl, title, childName, age, byLine, description }.
 *
 * @param {{ limit?: number }} [options]  — optional max rows (1–200, default 100 server-side)
 */
export async function getOpenPirateFlagGalleryScreen(options = {}) {
  const limit = options.limit;
  const q =
    limit != null && Number.isFinite(Number(limit))
      ? `?limit=${encodeURIComponent(String(Math.min(Math.max(Number(limit), 1), 200)))}`
      : '';
  const response = await fetch(`${API_BASE}/open/pirate-flag-gallery${q}`, {
    method: 'GET',
    headers,
  });
  return handleResponse(response);
}

/**
 * Fetch every entry regardless of status.  Used by the Security Centre moderation panel.
 * Returns an array of entry objects.
 */
export async function getAllFlags() {
  const response = await fetch(`${API_BASE}/pirate-flags/all`, {
    method: 'GET',
    headers,
  });
  const json = await handleResponse(response);
  return json.data ?? [];
}

/**
 * Submit a new pirate sail design entry.
 *
 * @param {Object} submission
 * @param {string} submission.childName
 * @param {number} submission.age
 * @param {string} submission.email       — parent/guardian contact (stored server-side; not exposed on public GET)
 * @param {string} [submission.school]    — optional school name
 * @param {string} submission.title
 * @param {string} submission.description
 * @param {string} submission.imageData       — base64 data-URI (data:image/...;base64,...)
 * @param {string} submission.imageFileName   — original filename with extension
 * @returns {Promise<{id: number, status: string, message: string, childName: string, title: string}>}
 */
export async function submitFlag(submission) {
  const response = await fetch(`${API_BASE}/pirate-flags`, {
    method: 'POST',
    headers,
    body: JSON.stringify(submission),
  });
  const json = await handleResponse(response);
  return json.data;
}

/**
 * Update the review status of a submission.
 *
 * @param {number}  id      — submission ID
 * @param {string}  status  — 'pending' | 'approved' | 'rejected'
 */
export async function updateFlagStatus(id, status) {
  const response = await fetch(`${API_BASE}/pirate-flags/${id}/status`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ status }),
  });
  return handleResponse(response);
}

/**
 * Permanently delete a submission (removes DB row + image file on server).
 *
 * @param {number} id — submission ID
 */
export async function deleteFlag(id) {
  const response = await fetch(`${API_BASE}/pirate-flags/${id}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
}
